import { ReactNode, useState } from "react";
import { Button, CircularProgress, ListItemIcon, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	AddIcon,
	DownloadIcon,
	EditIcon,
	LinkIconRoot,
	LinkUnitIcon,
	ReloadIcon,
	UploadIcon,
} from "../../../../assets/icons";

export type PageActionMenuItem = {
	label: string;
	icon?: ReactNode;
	onClick: VoidFunction;
	disabled?: boolean;
};

export type PageAction = {
	label: string;
	action?: {
		key: string;
		function?: VoidFunction;
	};
	loading?: boolean;
	menuItems?: PageActionMenuItem[];
	disabled?: boolean;
	icon?: ReactNode;
};

interface Props {
	pageActions?: PageAction[];
}

const PageHeaderActions = ({ pageActions }: Props) => {
	const { t } = useTranslation("common");
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [currentMenuItems, setCurrentMenuItems] = useState<PageActionMenuItem[] | null>(null);

	const open = Boolean(anchorEl);

	const toggleMenu = (event: React.MouseEvent<HTMLButtonElement>, menuItems: PageActionMenuItem[]) => {
		if (open) {
			setAnchorEl(null);
			setCurrentMenuItems(null);
		} else {
			setAnchorEl(event.currentTarget);
			setCurrentMenuItems(menuItems);
		}
	};

	const hideMenu = () => {
		setAnchorEl(null);
		setCurrentMenuItems(null);
	};

	const getIcon = (action: PageAction) => {
		if (action.loading) return <CircularProgress size={18} />;
		if (action.icon) return action.icon;

		switch (action.action?.key) {
			case "download":
				return <DownloadIcon color={action.disabled ? "disabled" : "primary"} />;
			case "edit":
				return <EditIcon color={action.disabled ? "disabled" : "primary"} />;
			case "add":
				return <AddIcon color="inherit" />;
			case "open":
				return <UploadIcon color={action.disabled ? "disabled" : "primary"} />;
			case "link":
				return <LinkUnitIcon color="primary" />;
			case "linkRootCause":
				return <LinkIconRoot color="inherit" />;
			case "refresh":
				return <ReloadIcon color={action.disabled ? "disabled" : "primary"} />;
			default:
				return null;
		}
	};

	return (
		<Stack>
			<Stack
				sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}
				spacing={2}
				direction="row"
			>
				<Stack direction="row" spacing={2}>
					{pageActions?.map((action) => {
						// Button with menu
						if (action.menuItems && action.menuItems.length > 0) {
							return (
								<Stack key={action.label}>
									<Button
										disabled={action.disabled || action.loading}
										onClick={(e) => toggleMenu(e, action.menuItems!)}
										color="primary"
										startIcon={getIcon(action)}
									>
										{t(action.label)}
									</Button>

									<Menu
										anchorEl={anchorEl}
										open={open && currentMenuItems === action.menuItems}
										onClose={hideMenu}
									>
										{currentMenuItems?.map((item) => (
											<MenuItem
												key={item.label}
												disabled={item.disabled}
												onClick={() => {
													item.onClick();
													hideMenu();
												}}
											>
												{item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
												<Typography>{t(item.label)}</Typography>
											</MenuItem>
										))}
									</Menu>
								</Stack>
							);
						}

						// Simple button without menu
						return (
							<Button
								key={action.label}
								variant={
									action.action?.key === "add" ||
									action.action?.key === "linkRootCause" ||
									action.action?.key === "calculate"
										? "contained"
										: "text"
								}
								disabled={action.disabled || action.loading}
								onClick={action.action?.function}
								color="primary"
								startIcon={getIcon(action)}
								sx={{ whiteSpace: "nowrap" }}
							>
								{t(action.label)}
							</Button>
						);
					})}
				</Stack>
			</Stack>
		</Stack>
	);
};

export default PageHeaderActions;
